import {useEffect, useCallback, useState} from 'react';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import './mcp-page.css';

export function McpPage() {
  // Load fonts
  useEffect(() => {
    const fontsLink = document.createElement('link');
    fontsLink.href =
      'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=Playfair+Display:wght@500;600;700&family=DM+Mono:wght@400;500&display=swap';
    fontsLink.rel = 'stylesheet';
    document.head.appendChild(fontsLink);
    return () => {
      fontsLink.remove();
    };
  }, []);

  // Intersection Observer for fade-up animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {threshold: 0.1, rootMargin: '0px 0px -40px 0px'},
    );
    const fadeElements = document.querySelectorAll('.fade-up');
    fadeElements.forEach(el => observer.observe(el));
    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  // Smooth scroll for anchor links
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (href) {
          const el = document.querySelector(href);
          if (el) {
            e.preventDefault();
            el.scrollIntoView({behavior: 'smooth', block: 'start'});
          }
        }
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Handle anchor scrolling on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
      }, 100);
    }
  }, []);

  const [copyText, setCopyText] = useState('Copy URL');
  const [copied, setCopied] = useState(false);
  const [showAuthLightbox, setShowAuthLightbox] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText('https://mcp.landed.net/mcp').then(() => {
      setCopyText('Copied!');
      setCopied(true);
      setTimeout(() => {
        setCopyText('Copy URL');
        setCopied(false);
      }, 2000);
    });
  }, []);

  return (
    <div className="landing-page mcp-page">
      <DefaultMetaTags />

      {/* HERO */}
      <section className="mcp-hero">
        <div className="mcp-hero-bg">
          <div className="mcp-hero-grid" />
        </div>
        <div className="mcp-hero-content">
          <div className="mcp-hero-text fade-up">
            <div className="hero-badge">
              <div className="pulse" /> MCP Integration
            </div>
            <h1>
              Build websites through <em>conversation</em>
            </h1>
            <p className="mcp-hero-sub">
              Connect your AI assistant to Landed.net using the Model Context
              Protocol. Create, manage, and publish websites without ever
              leaving your chat.
            </p>
            <div className="mcp-hero-actions">
              <a href="/dashboard" className="btn-primary">
                Get Started{' '}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
              <a href="#commands" className="btn-secondary">
                View Commands
              </a>
            </div>
            <div className="server-url-bar">
              <div className="url-icon">&#9889;</div>
              <code>https://mcp.landed.net/mcp</code>
              <button
                className={`copy-btn${copied ? ' copied' : ''}`}
                onClick={handleCopy}
              >
                {copyText}
              </button>
            </div>
          </div>
          <div
            className="mcp-hero-visual fade-up"
            style={{transitionDelay: '0.15s', position: 'relative'}}
          >
            <div className="floating-badge badge-top">
              <div className="badge-icon mcp">&#128268;</div>
              <div className="badge-text">
                MCP Connected<span>5 tools available</span>
              </div>
            </div>
            <div className="terminal-frame">
              <div className="terminal-bar">
                <div className="terminal-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="terminal-title">
                  Claude &mdash; Landed.net MCP
                </div>
              </div>
              <div className="terminal-body">
                <div className="line">
                  <span className="comment"># Create a site with AI</span>
                </div>
                <div className="line">
                  <span className="prompt">You: </span>
                  <span className="cmd">
                    Create a website for my bakery
                  </span>
                </div>
                <div className="line">
                  <span className="cmd">
                    &nbsp;&nbsp;&nbsp;&nbsp;called &quot;Sweet Rise&quot; with a
                  </span>
                </div>
                <div className="line">
                  <span className="cmd">
                    &nbsp;&nbsp;&nbsp;&nbsp;warm, inviting design.
                  </span>
                </div>
                <div className="line">&nbsp;</div>
                <div className="line">
                  <span className="response">Claude: </span>
                  <span className="muted">Creating your site now...</span>
                </div>
                <div className="line">&nbsp;</div>
                <div className="line">
                  <span className="muted">&rarr; </span>
                  <span className="key">create_site</span>
                  <span className="muted">(</span>
                </div>
                <div className="line">
                  <span className="muted">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span className="key">name</span>
                  <span className="muted">: </span>
                  <span className="string">
                    &quot;Sweet Rise Bakery&quot;
                  </span>
                </div>
                <div className="line">
                  <span className="muted">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span className="key">slug</span>
                  <span className="muted">: </span>
                  <span className="string">&quot;sweet-rise&quot;</span>
                </div>
                <div className="line">
                  <span className="muted">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span className="key">published</span>
                  <span className="muted">: </span>
                  <span className="response">true</span>
                </div>
                <div className="line">
                  <span className="muted">)</span>
                </div>
                <div className="line">&nbsp;</div>
                <div className="line">
                  <span className="response">&#10003; </span>
                  <span className="cmd">Site live at </span>
                  <span className="string">sweet-rise.landed.net</span>
                  <span className="cursor-blink" />
                </div>
              </div>
            </div>
            <div className="floating-badge badge-bottom">
              <div className="badge-icon ai">&#10024;</div>
              <div className="badge-text">
                AI-Powered<span>Natural language control</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPATIBILITY */}
      <section className="mcp-section compat-section" id="compatibility">
        <div className="compat-header fade-up">
          <div className="section-label">Compatibility</div>
          <h2 className="section-title">Works with your favorite AI</h2>
          <p className="section-sub">
            The Landed MCP server is available now on Claude, with ChatGPT
            support arriving by the end of March 2026. Any MCP-compatible
            client can connect via the API.
          </p>
        </div>
        <div className="compat-grid">
          <div className="compat-card available fade-up">
            <div className="compat-icon claude">&#129505;</div>
            <h3>Claude</h3>
            <p>
              Available now on Claude.ai, Claude Desktop, and Claude Code.
              Connect as a custom connector or use the API with the MCP beta
              header.
            </p>
            <div className="compat-status live">
              <span className="status-dot" /> Available Now
            </div>
          </div>
          <div
            className="compat-card coming-soon fade-up"
            style={{transitionDelay: '0.1s'}}
          >
            <div className="compat-icon chatgpt">&#128154;</div>
            <h3>ChatGPT</h3>
            <p>
              Full MCP support for ChatGPT is on the way. Connect Landed.net
              as a remote MCP server directly in ChatGPT&apos;s connector
              settings.
            </p>
            <div className="compat-status soon">
              <span className="status-dot" /> End of March 2026
            </div>
          </div>
          <div
            className="compat-card available fade-up"
            style={{transitionDelay: '0.2s'}}
          >
            <div className="compat-icon api">&#128279;</div>
            <h3>API / Custom Clients</h3>
            <p>
              Any MCP-compatible client can connect. Supports both Streamable
              HTTP and SSE transports over the standard protocol.
            </p>
            <div className="compat-status live">
              <span className="status-dot" /> Available Now
            </div>
          </div>
        </div>
      </section>

      {/* COMMANDS */}
      <section className="mcp-section commands-section" id="commands">
        <div className="commands-header fade-up">
          <div className="section-label">MCP Commands</div>
          <h2 className="section-title">
            Five powerful tools at your fingertips
          </h2>
          <p className="section-sub">
            The Landed MCP server exposes five tools that cover the full
            lifecycle of a website &mdash; from creation to publication.
          </p>
        </div>
        <div className="commands-grid">
          <div className="command-card fade-up">
            <div className="command-header">
              <div className="command-icon create">&#127959;&#65039;</div>
              <div>
                <div className="command-name">create_site</div>
                <div className="command-label">Create a new website</div>
              </div>
            </div>
            <p className="command-desc">
              Generate a brand-new Landed.net site from scratch. Provide a
              name, description, prompt, style preferences, colors, page
              sections, social links, contact details, and more. The AI
              endpoint handles design and content generation automatically.
            </p>
            <div className="command-params">
              <span className="param-tag required">name</span>
              <span className="param-tag">slug</span>
              <span className="param-tag">description</span>
              <span className="param-tag">prompt</span>
              <span className="param-tag">siteStyle</span>
              <span className="param-tag">colors[]</span>
              <span className="param-tag">pages[]</span>
              <span className="param-tag">pageSections[]</span>
              <span className="param-tag">category</span>
              <span className="param-tag">socials[]</span>
              <span className="param-tag">contactDetails[]</span>
              <span className="param-tag">headerContent</span>
              <span className="param-tag">footerContent</span>
              <span className="param-tag">published</span>
              <span className="param-tag">templateName</span>
            </div>
          </div>
          <div
            className="command-card fade-up"
            style={{transitionDelay: '0.08s'}}
          >
            <div className="command-header">
              <div className="command-icon status">&#128202;</div>
              <div>
                <div className="command-name">get_site_status</div>
                <div className="command-label">Check project details</div>
              </div>
            </div>
            <p className="command-desc">
              Retrieve the current status and details for any project by its
              ID. Use the optional <code>loader</code> parameter set to{' '}
              <code>&quot;editor&quot;</code> to get the full editor-ready
              payload including pages, styles, and configuration.
            </p>
            <div className="command-params">
              <span className="param-tag required">projectId</span>
              <span className="param-tag">loader</span>
            </div>
          </div>
          <div
            className="command-card fade-up"
            style={{transitionDelay: '0.16s'}}
          >
            <div className="command-header">
              <div className="command-icon update">&#9999;&#65039;</div>
              <div>
                <div className="command-name">update_site</div>
                <div className="command-label">Modify existing content</div>
              </div>
            </div>
            <p className="command-desc">
              Update an existing Landed.net project&apos;s content and
              settings. Change the site name, swap out the HTML template,
              inject custom CSS or JavaScript, update custom element styles,
              or replace individual pages.
            </p>
            <div className="command-params">
              <span className="param-tag required">projectId</span>
              <span className="param-tag">name</span>
              <span className="param-tag">template</span>
              <span className="param-tag">css</span>
              <span className="param-tag">customElementCss</span>
              <span className="param-tag">js</span>
              <span className="param-tag">pages[]</span>
            </div>
          </div>
          <div
            className="command-card fade-up"
            style={{transitionDelay: '0.24s'}}
          >
            <div className="command-header">
              <div className="command-icon publish">&#128640;</div>
              <div>
                <div className="command-name">publish_site</div>
                <div className="command-label">Go live or unpublish</div>
              </div>
            </div>
            <p className="command-desc">
              Publish or unpublish a Landed.net project. You can also
              configure the custom domain, set a forms notification email for
              contact form submissions, and define a webhook URL for event
              callbacks.
            </p>
            <div className="command-params">
              <span className="param-tag required">projectId</span>
              <span className="param-tag">published</span>
              <span className="param-tag">domainId</span>
              <span className="param-tag">formsEmail</span>
              <span className="param-tag">webhook</span>
            </div>
          </div>
          <div
            className="command-card full-width fade-up"
            style={{transitionDelay: '0.32s'}}
          >
            <div className="command-header">
              <div className="command-icon templates">&#128203;</div>
              <div>
                <div className="command-name">list_templates</div>
                <div className="command-label">
                  Browse available templates
                </div>
              </div>
            </div>
            <p className="command-desc">
              Browse the full library of Landed.net templates with optional
              filters. Search by keyword, filter by category, control sort
              order and pagination. Useful for discovering designs before
              creating a new site, or for selecting a template to apply to an
              existing project.
            </p>
            <div className="command-params">
              <span className="param-tag">query</span>
              <span className="param-tag">category</span>
              <span className="param-tag">orderBy</span>
              <span className="param-tag">orderDir</span>
              <span className="param-tag">page</span>
              <span className="param-tag">perPage</span>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mcp-section how-section" id="setup">
        <div className="how-header fade-up">
          <div className="section-label">Setup</div>
          <h2 className="section-title">Connect in under a minute</h2>
          <p className="section-sub">
            Getting started with the Landed MCP server is quick and
            straightforward &mdash; no coding required.
          </p>
        </div>
        <div className="steps-grid">
          <div className="step-card fade-up">
            <div className="step-num">01</div>
            <h3>Open Connectors</h3>
            <p>
              In Claude.ai, go to Settings &rarr; Connectors, then click
              &quot;Add custom connector&quot; at the bottom. On Claude
              Desktop, navigate to Settings &rarr; Connectors and add from
              there.
            </p>
          </div>
          <div className="step-card fade-up" style={{transitionDelay: '0.1s'}}>
            <div className="step-num">02</div>
            <h3>Paste the Server URL</h3>
            <p>
              Enter the Landed MCP server URL:
              <br />
              <code
                style={{
                  color: 'var(--pink-light)',
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '13px',
                }}
              >
                https://mcp.landed.net/mcp
              </code>
              <br />
              Then complete the OAuth sign-in to authenticate your Landed.net
              account.
            </p>
            <button
              className="auth-screenshot-btn"
              onClick={() => setShowAuthLightbox(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              See authorization screen
            </button>
          </div>
          <div className="step-card fade-up" style={{transitionDelay: '0.2s'}}>
            <div className="step-num">03</div>
            <h3>Start Building</h3>
            <p>
              That&apos;s it. Open a new conversation and ask Claude to create
              a website, check a project&apos;s status, update content, or
              publish your site &mdash; all through natural language.
            </p>
          </div>
        </div>
      </section>

      {/* API EXAMPLE */}
      <section className="mcp-section code-section" id="api">
        <div
          className="section-label"
          style={{justifyContent: 'center'}}
        >
          API Integration
        </div>
        <h2
          className="section-title"
          style={{textAlign: 'center', marginLeft: 'auto', marginRight: 'auto'}}
        >
          Use the MCP connector via the API
        </h2>
        <p
          className="section-sub"
          style={{textAlign: 'center', marginLeft: 'auto', marginRight: 'auto'}}
        >
          Developers can connect to the Landed MCP server programmatically
          through the Anthropic Messages API using the MCP connector beta.
        </p>
        <div className="code-block-wrapper fade-up">
          <div className="code-block">
            <div className="code-block-bar">
              <div className="dots">
                <span />
                <span />
                <span />
              </div>
              <div className="filename">api-example.js</div>
            </div>
            <pre>
              <span className="comment">
                {
                  '// Connect to Landed.net MCP via the Anthropic API'
                }
              </span>
              {'\n\n'}
              <span className="key">const</span>
              {' response = '}
              <span className="key">await</span>
              {' fetch('}
              <span className="string">
                &quot;https://api.anthropic.com/v1/messages&quot;
              </span>
              {', '}
              <span className="brace">{'{'}</span>
              {'\n  '}
              <span className="key">method</span>
              {': '}
              <span className="string">&quot;POST&quot;</span>
              {',\n  '}
              <span className="key">headers</span>
              {': '}
              <span className="brace">{'{'}</span>
              {'\n    '}
              <span className="string">
                &quot;Content-Type&quot;
              </span>
              {': '}
              <span className="string">
                &quot;application/json&quot;
              </span>
              {',\n    '}
              <span className="string">&quot;X-API-Key&quot;</span>
              {': process.env.'}
              <span className="key">ANTHROPIC_API_KEY</span>
              {',\n    '}
              <span className="string">
                &quot;anthropic-version&quot;
              </span>
              {': '}
              <span className="string">
                &quot;2023-06-01&quot;
              </span>
              {',\n    '}
              <span className="string">
                &quot;anthropic-beta&quot;
              </span>
              {': '}
              <span className="string">
                &quot;mcp-client-2025-11-20&quot;
              </span>
              {'\n  '}
              <span className="brace">{'}'}</span>
              {',\n  '}
              <span className="key">body</span>
              {': JSON.stringify('}
              <span className="brace">{'{'}</span>
              {'\n    '}
              <span className="key">model</span>
              {': '}
              <span className="string">
                &quot;claude-sonnet-4-6&quot;
              </span>
              {',\n    '}
              <span className="key">max_tokens</span>
              {': '}
              <span className="bool">1024</span>
              {',\n    '}
              <span className="key">messages</span>
              {': ['}
              <span className="brace">{'{'}</span>
              {'\n      '}
              <span className="key">role</span>
              {': '}
              <span className="string">&quot;user&quot;</span>
              {',\n      '}
              <span className="key">content</span>
              {': '}
              <span className="string">
                &quot;Create a website for my coffee shop&quot;
              </span>
              {'\n    '}
              <span className="brace">{'}'}</span>
              {'],\n    '}
              <span className="key">mcp_servers</span>
              {': ['}
              <span className="brace">{'{'}</span>
              {'\n      '}
              <span className="key">type</span>
              {': '}
              <span className="string">&quot;url&quot;</span>
              {',\n      '}
              <span className="key">url</span>
              {': '}
              <span className="string">
                &quot;https://mcp.landed.net/mcp&quot;
              </span>
              {',\n      '}
              <span className="key">name</span>
              {': '}
              <span className="string">
                &quot;landed-mcp&quot;
              </span>
              {',\n      '}
              <span className="key">authorization_token</span>
              {': '}
              <span className="string">
                &quot;YOUR_OAUTH_TOKEN&quot;
              </span>
              {'\n    '}
              <span className="brace">{'}'}</span>
              {'],\n    '}
              <span className="key">tools</span>
              {': ['}
              <span className="brace">{'{'}</span>
              {'\n      '}
              <span className="key">type</span>
              {': '}
              <span className="string">
                &quot;mcp_toolset&quot;
              </span>
              {',\n      '}
              <span className="key">mcp_server_name</span>
              {': '}
              <span className="string">
                &quot;landed-mcp&quot;
              </span>
              {'\n    '}
              <span className="brace">{'}'}</span>
              {']\n  '}
              <span className="brace">{'}'}</span>
              {')\n'}
              <span className="brace">{'}'}</span>
              {');'}
            </pre>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="mcp-section use-cases-section" id="use-cases">
        <div className="use-cases-header fade-up">
          <div className="section-label">Use Cases</div>
          <h2 className="section-title">What you can build</h2>
          <p className="section-sub">
            From quick landing pages to full business sites &mdash; all
            through conversation with your AI assistant.
          </p>
        </div>
        <div className="use-cases-grid">
          <div className="use-case-card fade-up">
            <span className="use-case-emoji">&#127978;</span>
            <h3>Launch a Business Site</h3>
            <p>
              Describe your business and let AI generate a complete, branded
              website with custom copy, layout, and design &mdash; published
              in seconds.
            </p>
            <div className="use-case-example">
              &quot;Create a website for my dog grooming business in Austin
              called Paws &amp; Claws&quot;
            </div>
          </div>
          <div
            className="use-case-card fade-up"
            style={{transitionDelay: '0.08s'}}
          >
            <span className="use-case-emoji">&#127912;</span>
            <h3>Rapid Prototyping</h3>
            <p>
              Spin up landing pages to test ideas, validate concepts, or mock
              up client work. Iterate by updating content and styles through
              follow-up messages.
            </p>
            <div className="use-case-example">
              &quot;Build a landing page for a new SaaS product called
              Flowline with a dark theme&quot;
            </div>
          </div>
          <div
            className="use-case-card fade-up"
            style={{transitionDelay: '0.16s'}}
          >
            <span className="use-case-emoji">&#128230;</span>
            <h3>Portfolio &amp; Resume Sites</h3>
            <p>
              Generate a polished personal site or portfolio. Add your
              projects, skills, and contact info. Publish instantly with a
              clean URL.
            </p>
            <div className="use-case-example">
              &quot;Make a portfolio site for a UX designer named Sarah Chen
              with a minimal aesthetic&quot;
            </div>
          </div>
          <div
            className="use-case-card fade-up"
            style={{transitionDelay: '0.24s'}}
          >
            <span className="use-case-emoji">&#128260;</span>
            <h3>Bulk Site Management</h3>
            <p>
              Manage multiple sites from a single conversation. Check
              statuses, push updates, and toggle publishing across your
              entire portfolio of projects.
            </p>
            <div className="use-case-example">
              &quot;Check the status of project 1234 and update its footer
              with the new phone number&quot;
            </div>
          </div>
          <div
            className="use-case-card fade-up"
            style={{transitionDelay: '0.32s'}}
          >
            <span className="use-case-emoji">&#9889;</span>
            <h3>Custom Code Injection</h3>
            <p>
              Push custom CSS, JavaScript, or entirely custom HTML templates
              into any existing project. Perfect for developers who want
              fine-grained control.
            </p>
            <div className="use-case-example">
              &quot;Add a custom CSS animation to the hero section of my
              project and inject a tracking script&quot;
            </div>
          </div>
          <div
            className="use-case-card fade-up"
            style={{transitionDelay: '0.4s'}}
          >
            <span className="use-case-emoji">&#128231;</span>
            <h3>Form &amp; Webhook Setup</h3>
            <p>
              Configure contact form notifications and webhook endpoints
              through the publish command. Integrate with your existing
              workflows seamlessly.
            </p>
            <div className="use-case-example">
              &quot;Publish my site and set the forms email to
              hello@mybusiness.com with a webhook to Zapier&quot;
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mcp-cta-section">
        <div className="mcp-cta-content fade-up">
          <h2>Ready to connect?</h2>
          <p>
            Start building websites through conversation. Add the Landed MCP
            server to Claude today &mdash; it takes less than a minute.
          </p>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <a href="/dashboard" className="btn-primary">
              Start Building Free{' '}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
            <a href="/dashboard/api" className="btn-secondary">
              API Documentation
            </a>
          </div>
        </div>
      </section>

      {/* Auth Screenshot Lightbox */}
      <div
        className={`mcp-lightbox-overlay${showAuthLightbox ? ' active' : ''}`}
        onClick={() => setShowAuthLightbox(false)}
      >
        {showAuthLightbox && (
          <div className="mcp-lightbox-box" onClick={e => e.stopPropagation()}>
            <button
              className="mcp-lightbox-close"
              onClick={() => setShowAuthLightbox(false)}
            >
              &#x2715;
            </button>
            <div className="mcp-lightbox-content">
              <img
                src="/images/mcp-lightbox.jpg"
                alt="Landed MCP authorization screen showing permission request for reading projects, templates, account info, and creating/editing projects"
              />
            </div>
            <div className="mcp-lightbox-caption">
              <h3>Authorization Request</h3>
              <p>
                When you connect the Landed MCP server, you&apos;ll see this
                authorization screen. Click &quot;Authorize&quot; to grant
                access to your Landed.net account.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
